<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { endOfDay, getUnixTime, startOfDay } from 'date-fns';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import {
  DATE_RANGE_TYPES,
  dateRanges,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  PERIOD_PRESETS,
  isRollingRange,
  nameFitsDates,
  periodChip,
  periodFromRange,
  periodLabel,
} from './period';

// UI-FILTROS-01 (§2.4, D8): o período das barras de filtro, um só para toda tela. Os chips são o
// TabBar; "Personalizado" mostra o DatePicker, com os atalhos dele na lista lateral. Um período
// que não é chip (os 6 meses da lista lateral, ou um link de outro relatório) fica em
// "Personalizado", com o nome dele no chip.
const props = defineProps({
  // Os nomes de DATE_RANGE_TYPES que viram chip.
  presets: { type: Array, default: () => PERIOD_PRESETS },
  // Sem "Personalizado", o período fora dos chips não teria onde aparecer: nenhum chip pressionado,
  // e nenhum calendário. Quem normaliza é a tela, e não o seletor, que emitiria ao montar e
  // reescreveria a URL no meio da navegação de quem abriu o link. A tela lê com o
  // `periodCodec({ presets })` dos mesmos chips, que devolve o padrão dela no lugar, e não passa
  // `minDate` nem `maxDate`: o chip cortado por eles vira personalizado.
  allowCustom: { type: Boolean, default: true },
  // O período escolhido aqui (chip, lista lateral ou calendário) não sai destes dias. Cortado, o
  // nome deixa de dizer as datas, e ele fica personalizado. O que chega de fora (a URL, a tela)
  // não é cortado: a tela que passa limites corta o valor que ela mesma dá.
  minDate: { type: Date, default: null },
  maxDate: { type: Date, default: null },
});

// { range, from, to }: `from` e `to` em segundos unix, do início ao fim do dia.
const period = defineModel({ type: Object, required: true });

const { t } = useI18n();
const { CUSTOM_RANGE } = DATE_RANGE_TYPES;

// O período como o seletor o mostra. O nome que não diz mais as datas (o link antigo de outro
// relatório, que grava "últimos 30 dias" junto com as datas de agosto) fica em "Personalizado", com
// as datas no DatePicker: o chip dele não fica pressionado sobre uma janela que não é a dele.
const shown = computed(() =>
  nameFitsDates(period.value)
    ? period.value
    : { ...period.value, range: CUSTOM_RANGE }
);

// O nome que os chips mostram. "Este mês" levado para agosto pelo ◀ do DatePicker guarda o nome
// (`shown`), para as setas seguirem de onde estão, mas o chip não diz "Este mês" sobre agosto: fica
// "Personalizado", e o DatePicker ao lado diz o mês (ou a semana).
const chipRange = computed(() => periodChip(period.value));

const outsideChips = computed(() => !props.presets.includes(chipRange.value));

const tabs = computed(() => [
  ...props.presets.map(range => ({ range, label: periodLabel({ range }, t) })),
  ...(props.allowCustom
    ? [
        {
          range: CUSTOM_RANGE,
          label: outsideChips.value
            ? periodLabel({ range: chipRange.value }, t)
            : t('FILTER_BAR.PERIOD.CUSTOM'),
        },
      ]
    : []),
]);

const activeTab = computed(() => {
  if (!outsideChips.value) return props.presets.indexOf(chipRange.value);
  return props.allowCustom ? props.presets.length : -1;
});

// O corte continua de dia inteiro: o período todo depois de maxDate fica no último dia, e o todo
// antes de minDate, no primeiro.
const limits = computed(() => ({
  firstStart: props.minDate
    ? getUnixTime(startOfDay(props.minDate))
    : -Infinity,
  firstEnd: props.minDate ? getUnixTime(endOfDay(props.minDate)) : -Infinity,
  lastStart: props.maxDate ? getUnixTime(startOfDay(props.maxDate)) : Infinity,
  lastEnd: props.maxDate ? getUnixTime(endOfDay(props.maxDate)) : Infinity,
}));

const clamp = next => {
  const { firstStart, firstEnd, lastStart, lastEnd } = limits.value;
  const from = Math.min(Math.max(next.from, firstStart), lastStart);
  const to = Math.max(Math.min(next.to, lastEnd), firstEnd);
  if (from === next.from && to === next.to) return next;
  return { range: CUSTOM_RANGE, from, to };
};

const choose = next => {
  period.value = clamp(next);
};

// "Personalizado" parte das datas de agora: o DatePicker sem valor abriria nos últimos 7 dias.
const onTabChanged = tab => {
  if (tab.range !== CUSTOM_RANGE) {
    choose(periodFromRange(tab.range));
    return;
  }
  if (outsideChips.value) return;
  choose({
    range: CUSTOM_RANGE,
    from: period.value.from,
    to: period.value.to,
  });
};

// A lista lateral tem os atalhos. Os rolantes (os chips, os 6 meses) vêm com as datas de hoje, e
// não com as do DatePicker, que são as do dia em que ele montou: aberto ontem, ele daria a janela
// de ontem. Um que também é chip vira o chip. "Este mês" e "esta semana" guardam as datas do
// DatePicker (navegados para o mês passado). O dia escolhido no calendário chega à meia-noite, e o
// fim vai para o fim do dia.
const onDateRangeChanged = ([start, end, rangeType]) => {
  if (isRollingRange(rangeType)) {
    choose(periodFromRange(rangeType));
    return;
  }
  choose({
    range: rangeType,
    from: getUnixTime(startOfDay(start)),
    to: getUnixTime(endOfDay(end)),
  });
};

// O DatePicker só conhece os nomes da lista lateral dele: com outro (90 dias fora dos chips) o
// botão dele ficaria sem rótulo. E ele sempre recebe as datas, senão abre nos últimos 7 dias.
const pickerRange = computed(() =>
  dateRanges.some(item => item.value === shown.value.range)
    ? shown.value.range
    : CUSTOM_RANGE
);
const pickerDates = computed(() => [
  new Date(period.value.from * 1000),
  new Date(period.value.to * 1000),
]);

// A faixa rola quando não cabe (chips a mais, ou um nome longo da lista lateral no último): o chip
// pressionado rola até ficar à vista, sem mexer na rolagem da página.
const strip = ref(null);
const showPressed = () => {
  const box = strip.value;
  const pressed = box?.querySelector('button[aria-pressed="true"]');
  if (!pressed) return;
  const inside = box.getBoundingClientRect();
  const chip = pressed.getBoundingClientRect();
  if (chip.right > inside.right) box.scrollLeft += chip.right - inside.right;
  else if (chip.left < inside.left) box.scrollLeft -= inside.left - chip.left;
};
onMounted(showPressed);
watch([activeTab, tabs], showPressed, { flush: 'post' });
</script>

<template>
  <div
    role="group"
    :aria-label="t('FILTER_BAR.PERIOD.TITLE')"
    class="flex flex-wrap gap-2 items-center min-w-0 max-w-full"
  >
    <!-- O `overflow-x-auto` também corta em cima e embaixo: sem folga, o contorno do chip pressionado
    saía cortado, e o zoom do clique abria uma barra de rolagem que empurrava a linha. A folga de
    4 px (`p-1`) volta pelo `-m-1`, e o `max-w` a soma: os chips ficam onde estavam, com a mesma
    largura à vista. -->
    <div ref="strip" class="overflow-x-auto p-1 -m-1 max-w-[calc(100%+0.5rem)]">
      <TabBar
        compact
        :tabs="tabs"
        :initial-active-tab="activeTab"
        @tab-changed="onTabChanged"
      />
    </div>
    <WootDatePicker
      v-if="allowCustom && outsideChips"
      :date-range="pickerDates"
      :range-type="pickerRange"
      @date-range-changed="onDateRangeChanged"
    />
  </div>
</template>
